import React, { useMemo } from 'react';
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import EmployeePortalCrudShell from './EmployeePortalCrudShell';
import {
  LEAVE_STATUS,
  statusBadge,
  defaultStats,
  countByNormalizedStatus,
  normalizeLeaveStatus,
  getLeaveTypeLabel,
  formatPortalDate,
} from './employeePortalShared';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

const NS = 'leavesPage';
const API = '/api/hr/leaves';

const EMPTY = {
  leaveType: '', startDate: '', endDate: '', days: '', status: '', reason: '', comments: '',
};

export default function EmployeePortalLeavesPage() {
  const { employeeId } = usePortalEmployeeContext();
  const apiQueryParams = useMemo(() => (employeeId ? { employee_id: employeeId } : {}), [employeeId]);

  const leaveOpts = LEAVE_STATUS.map((s) => ({ value: s.value, labelKey: s.labelKey }));

  const renderLeaveType = (r, _p, _t, pl) => getLeaveTypeLabel(r.leaveType || r.type, pl);
  const renderDate = (key) => (r) => formatPortalDate(r[key] || r[key === 'startDate' ? 'start_date' : 'end_date']);

  return (
    <EmployeePortalCrudShell
        ns={NS}
        icon={Calendar}
        apiPath={API}
        apiQueryParams={apiQueryParams}
        requireEmployeeScope
        normalizeStatus={normalizeLeaveStatus}
        allowCreate={false}
        allowEdit={false}
        allowDelete={false}
        gradient="from-emerald-500 to-emerald-700"
        emptyForm={EMPTY}
        mapRecordToForm={(r) => ({
          leaveType: r.leaveType || r.type || '',
          startDate: r.startDate || r.start_date || '',
          endDate: r.endDate || r.end_date || '',
          days: r.days != null ? String(r.days) : '',
          status: r.status || '',
          reason: r.reason || '',
          comments: r.comments || '',
        })}
        computeStats={(records) => defaultStats(records, {
          totalIcon: Calendar,
          fields: [
            {
              titleKey: 'stat_pending',
              icon: Clock,
              color: 'from-amber-500 to-amber-600',
              countFn: (recs) => recs.filter((r) => ['pending_manager', 'pending_hr', 'en_attente'].includes(normalizeLeaveStatus(r.status) || r.status)).length,
            },
            {
              titleKey: 'stat_approved',
              icon: CheckCircle,
              color: 'from-green-500 to-green-600',
              countFn: (recs) => countByNormalizedStatus(recs, 'status', 'approved', normalizeLeaveStatus),
            },
            {
              titleKey: 'stat_rejected',
              icon: XCircle,
              color: 'from-red-500 to-red-600',
              countFn: (recs) => countByNormalizedStatus(recs, 'status', 'rejected', normalizeLeaveStatus),
            },
            {
              titleKey: 'stat_cancelled',
              icon: AlertCircle,
              color: 'from-gray-500 to-gray-600',
              countFn: (recs) => countByNormalizedStatus(recs, 'status', 'cancelled', normalizeLeaveStatus),
            },
          ],
        })}
        columns={[
          { key: 'leaveType', labelKey: 'col_type', render: renderLeaveType },
          { key: 'startDate', labelKey: 'col_start', render: renderDate('startDate') },
          { key: 'endDate', labelKey: 'col_end', render: renderDate('endDate') },
          { key: 'days', labelKey: 'col_days' },
          { key: 'status', labelKey: 'col_status', render: (r, _p, _t, pl) => statusBadge(r.status, pl, LEAVE_STATUS) },
          { key: 'reason', labelKey: 'col_reason' },
        ]}
        formSections={[
          {
            id: 'leave',
            titleKey: 'section_leave',
            fields: [
              { key: 'leaveType', labelKey: 'col_type', readOnly: true },
              { key: 'startDate', labelKey: 'col_start', readOnly: true },
              { key: 'endDate', labelKey: 'col_end', readOnly: true },
              { key: 'days', labelKey: 'col_days', readOnly: true },
              { key: 'status', labelKey: 'col_status', type: 'select', options: leaveOpts, readOnly: true },
            ],
          },
          {
            id: 'details',
            titleKey: 'section_details',
            fields: [
              { key: 'reason', labelKey: 'col_reason', readOnly: true, fullWidth: true },
              { key: 'comments', labelKey: 'field_comments', type: 'textarea', readOnly: true, fullWidth: true },
            ],
          },
        ]}
      />
  );
}
